import { useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { firstLetterCapital } from "@/utils";
import { custom_context } from "@/utils/custom_context";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";

import { STEP_CONFIG, STEP_HEADERS } from "./constants";
import { getApplicationStepTooltip, getApplicationStepTooltipKey } from "./utils";
import { useApplicationStatus } from "./hooks/useApplicationStatus";

import ConnectSellerPlatform from "./components/connect_seller_platform";
import CreditEvaluation from "./components/credit_evaluation";
import AccountSetup from "./components/account_setup";
import DisbursalStatusCard from "./components/disbursal_status";
import AgreementSign from "./components/agreement_sign";
import KycCard from "./components/kyc_card";
import UploadDocumentsCard from "./components/upload_documents_card";
import StepperNavigation from "./components/StepperNavigation";
import StepContent from "./components/StepContent";
import DashboardErrorState from "./components/DashboardErrorState";

/**
 * Dashboard for a single application. A user can have multiple applications;
 * this page shows the one for the current URL (/los/:applicationId/apply).
 * When applicationId changes, we refetch and update ApplicationData context
 * so all children see the correct application's details.
 */
export function Dashboard() {
  const { applicationId: applicationUuid } = useParams();
  const stepperRef = useRef(null);
  const { profileData } = useContext(custom_context.ProfileData);

  const {
    applicationStatus,
    kycDetails,
    activeStep,
    agreementsDone,
    setAgreementsDone,
    loadError,
    setLoadError,
    stepStatus,
    loanStatus,
    fetchStatus,
    goNext,
    goBack,
  } = useApplicationStatus(applicationUuid);

  const userName = `${firstLetterCapital(profileData?.userDetails?.full_name)}`;

  const handleRetry = () => {
    setLoadError(null);
    fetchStatus();
  };

  if (loadError) {
    return <DashboardErrorState message={loadError} onRetry={handleRetry} />;
  }

  return (
    <section className="_welCome_fincoBox">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-12">
            <div className="outerFinco_welcome _kycCompletionBx">
              <div className="dash_containerDiv">
                <span className="_adminName" data-testid="fincobox-los-welcome-greeting">Hi, {userName}</span>

                <Stepper
                  ref={stepperRef}
                  style={{ flexBasis: "50rem" }}
                  orientation="vertical"
                  linear={true}
                  activeStep={activeStep}
                >
                  <StepperPanel header={STEP_HEADERS[STEP_CONFIG.APPLICATION]}>
                    <StepContent testId="fincobox-los-step-application">
                      <UploadDocumentsCard
                        loanDetails={applicationStatus?.loan}
                        refreshStatus={fetchStatus}
                      />
                      {(stepStatus.kycStepVerified || stepStatus.kycStepComplete || stepStatus.loanStepComplete) && (
                        <KycCard
                          loanDetails={applicationStatus?.loan}
                          kycDetails={kycDetails}
                          refreshStatus={fetchStatus}
                        />
                      )}
                      {((stepStatus.kycStepComplete || stepStatus.kycStepVerified) && stepStatus.loanStepComplete) && (
                        <ConnectSellerPlatform />
                      )}
                    </StepContent>
                    <StepperNavigation
                      onNext={goNext}
                      canGoBack={false}
                      canGoNext={stepStatus.applicationStepComplete}
                      nextDisabled={!stepStatus.applicationStepComplete}
                      nextTooltip={getApplicationStepTooltip(stepStatus)}
                      nextTooltipKey={getApplicationStepTooltipKey(stepStatus)}
                      nextTestId="fincobox-los-next-btn"
                    />
                  </StepperPanel>

                  <StepperPanel header={STEP_HEADERS[STEP_CONFIG.OFFER_AGREEMENT]}>
                    <StepContent testId="fincobox-los-step-offer-agreement">
                      <CreditEvaluation
                        disabled
                        documentStatus={loanStatus}
                      />
                      <AgreementSign
                        loanId={applicationStatus?.loan?.id}
                        refreshStatus={fetchStatus}
                        onStepStatusChange={setAgreementsDone}
                      />
                    </StepContent>
                    <StepperNavigation
                      onBack={goBack}
                      onNext={goNext}
                      canGoNext={agreementsDone}
                      nextDisabled={!agreementsDone}
                    />
                  </StepperPanel>

                  <StepperPanel header={STEP_HEADERS[STEP_CONFIG.ACCOUNT_SETUP]}>
                    <StepContent testId="fincobox-los-step-account-setup">
                      <AccountSetup loanId={applicationUuid} vaSetupRequired={applicationStatus?.feature_flags?.va_setup_required} />
                      <DisbursalStatusCard />
                    </StepContent>
                    <StepperNavigation onBack={goBack} showNext={false} />
                  </StepperPanel>
                </Stepper>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
